const TERMINAL_STATUSES = [
    "COMPLETED",
    "CANCELLED",
    "NO_SHOW",
    "MISSED",
    "SKIPPED",
];
export function isTerminalStatus(status) {
    return TERMINAL_STATUSES.includes((status ?? "").toUpperCase());
}
function toIso(value) {
    if (!value)
        return null;
    if (typeof value === "string")
        return value;
    if (typeof value === "number")
        return new Date(value).toISOString();
    return null;
}
function toEpochMs(value) {
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    if (typeof value === "string") {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
}
const BLOCKED_REASONS = [
    "ALREADY_CANCELLED",
    "ALREADY_COMPLETED",
    "ALREADY_NO_SHOW",
    "ALREADY_MISSED",
    "ALREADY_SKIPPED",
    "POLICY_DISALLOWS",
    "CUTOFF_PASSED",
];
const REFUND_CHANNELS = [
    "GATEWAY",
    "WALLET",
    "MANUAL",
    "NONE",
];
export function asBlockedReason(value) {
    const code = typeof value === "string" ? value.toUpperCase() : null;
    return code && BLOCKED_REASONS.includes(code)
        ? code
        : null;
}
function asRefundChannel(value) {
    const code = typeof value === "string" ? value.toUpperCase() : null;
    return code && REFUND_CHANNELS.includes(code)
        ? code
        : null;
}
function toNumber(value) {
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    if (typeof value === "string" && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}
function toBoolean(value) {
    return typeof value === "boolean" ? value : null;
}
function normalizeRefundPreview(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const eligibleAmount = toNumber(raw.eligibleAmount);
    const refundPercentage = toNumber(raw.refundPercentage);
    if (eligibleAmount == null && refundPercentage == null)
        return null;
    return {
        eligibleAmount,
        refundPercentage,
        currency: raw.currency ?? null,
        tierMatched: raw.tierMatched ?? null,
        refundChannel: asRefundChannel(raw.refundChannel),
    };
}
function fullName(parts) {
    const name = parts
        .filter((part) => typeof part === "string" && part.trim())
        .join(" ")
        .trim();
    return name || null;
}
export function normalizeAppointment(raw) {
    const appointment = raw?.appointment ?? raw ?? {};
    const doctor = raw?.doctorProfileDetail ?? {};
    const patient = raw?.patient ?? {};
    return {
        id: Number(appointment.id ?? raw?.id),
        patientId: Number(appointment.patientId ?? patient.id ?? 0),
        patientName: appointment.patientName ??
            fullName([patient.firstName, patient.middleName, patient.lastName]) ??
            null,
        doctorId: appointment.doctorId ?? doctor.id ?? null,
        doctorName: appointment.doctorName ??
            doctor.fullName ??
            fullName([doctor.firstName, doctor.lastName]) ??
            null,
        doctorSpecialization: Array.isArray(doctor.specialization)
            ? doctor.specialization.join(", ")
            : (appointment.doctorSpecialization ?? doctor.specialization ?? null),
        addressId: appointment.addressId ?? null,
        locationAddress: raw?.addressLine ?? raw?.locationAddress ?? appointment.locationAddress ?? null,
        mode: appointment.mode ?? null,
        status: appointment.status ?? null,
        appointmentDate: appointment.appointmentDate ?? null,
        fromDateTimeIso: appointment.fromDateTimeIso ??
            toIso(raw?.fromDateTimeTs) ??
            toIso(appointment.fromDateTimeTs),
        toDateTimeIso: appointment.toDateTimeIso ??
            toIso(raw?.toDateTimeTs) ??
            toIso(appointment.toDateTimeTs),
        endEpochMs: toEpochMs(appointment.toDateTimeTs) ??
            toEpochMs(raw?.toDateTimeTs) ??
            toEpochMs(appointment.toDateTimeIso) ??
            toEpochMs(appointment.shiftEnd),
        bookingReference: appointment.bookingReference ?? appointment.appointmentRef ?? null,
        chiefComplaint: appointment.chiefComplaint ?? appointment.reasonForVisit ?? null,
        paymentStatus: appointment.paymentStatus ?? null,
        paymentMethodName: appointment.paymentMethodName ?? null,
        paymentProvider: appointment.paymentProvider ?? null,
        paymentReference: appointment.paymentReference ?? null,
        paymentClaimedAt: appointment.paymentClaimedAt ?? null,
        paymentProofUrl: raw?.paymentProofUrl ?? null,
        paymentVerifiedAt: appointment.paymentVerifiedAt ?? null,
        paymentAmountDue: appointment.paymentAmountDue ?? appointment.totalFee ?? null,
        consultationCharge: appointment.consultationCharge ?? null,
        currency: appointment.currency ?? raw?.currency ?? null,
        videoMeetingUrl: raw?.videoMeetingUrl ?? appointment.videoMeetingUrl ?? null,
        videoMeetingProvider: appointment.videoMeetingProvider ?? null,
        bookedViaSessionPack: appointment.bookedViaSessionPack ?? null,
        source: appointment.source ?? null,
        type: appointment.type ?? null,
        qmsWorkShiftId: toNumber(appointment.qmsWorkShiftId),
        shiftStart: appointment.shiftStart ?? null,
        shiftEnd: appointment.shiftEnd ?? null,
        tokenNumber: toNumber(appointment.tokenNumber),
        isLive: toBoolean(appointment.isLive),
        canCancel: toBoolean(appointment.canCancel),
        canReschedule: toBoolean(appointment.canReschedule),
        cancelDisabledReason: asBlockedReason(appointment.cancelDisabledReason),
        rescheduleDisabledReason: asBlockedReason(appointment.rescheduleDisabledReason),
        refundPreview: normalizeRefundPreview(appointment.refundPreview),
        selfAttestedStatus: appointment.selfAttestedStatus === "COMPLETED" ||
            appointment.selfAttestedStatus === "NO_SHOW"
            ? appointment.selfAttestedStatus
            : null,
        selfAttestedAt: toNumber(appointment.selfAttestedAt),
    };
}
export function partitionByTab(appointments, now = Date.now()) {
    const upcoming = [];
    const past = [];
    for (const appointment of appointments) {
        const ended = appointment.endEpochMs != null && appointment.endEpochMs < now;
        if (isTerminalStatus(appointment.status) || ended) {
            past.push(appointment);
        }
        else {
            upcoming.push(appointment);
        }
    }
    const start = (a) => a.fromDateTimeIso ? Date.parse(a.fromDateTimeIso) : 0;
    const liveFirst = (a) => (a.isLive === true ? 0 : 1);
    upcoming.sort((a, b) => liveFirst(a) - liveFirst(b) || start(a) - start(b));
    past.sort((a, b) => start(b) - start(a));
    return { UPCOMING: upcoming, PAST: past };
}
